import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import { ensureCurrentUser } from "../features/auth/authSlice";
import DropdownChevron from "../components/dropdown-chevron";

// Mini sparkline chart component
const Sparkline = () => (
  <svg viewBox="0 0 80 30" className="w-16 h-8">
    <polyline
      points="0,25 15,20 30,22 45,15 60,10 75,5"
      fill="none"
      stroke="#d1d5db"
      strokeWidth="1.5"
    />
  </svg>
);

// Donut chart component
interface DonutChartProps {
  value: number;
  color?: string;
}

const DonutChart = ({ value, color = "#ABD405" }: DonutChartProps) => {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <svg viewBox="0 0 80 80" className="w-20 h-20">
      <circle cx="40" cy="40" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
      <circle
        cx="40"
        cy="40"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="45" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111827">
        {value}%
      </text>
    </svg>
  );
};

// Line chart for feedback over time
const FeedbackChart = () => {
  const points = [
    { x: 30, y: 30 },
    { x: 120, y: 75 },
    { x: 210, y: 110 },
    { x: 300, y: 140 },
    { x: 390, y: 160 },
  ];

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPoints = `${points[0].x},200 ${polylinePoints} ${points[points.length - 1].x},200`;

  const tooltip = points[2];

  return (
    <div className="relative">
      <svg viewBox="0 0 420 210" className="w-full h-48">
        {/* Grid lines */}
        {[30, 75, 120, 165].map((y, i) => (
          <line key={i} x1="20" y1={y + 15} x2="410" y2={y + 15} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4,4" />
        ))}

        {/* Y-axis labels */}
        {[4, 3, 2, 1].map((label, i) => (
          <text key={i} x="12" y={i * 45 + 50} fontSize="10" fill="#9ca3af" textAnchor="middle">{label}</text>
        ))}

        {/* Area fill */}
        <polygon points={areaPoints} fill="#f0fdf4" opacity="0.6" />

        {/* Confidence band */}
        <polygon
          points="30,15 120,60 210,95 300,125 390,145 390,175 300,155 210,125 120,90 30,60"
          fill="#d1fae5"
          opacity="0.5"
        />

        {/* Main line */}
        <polyline points={polylinePoints} fill="none" stroke="#4b5563" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Data points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#4b5563" />
        ))}

        {/* Tooltip */}
        <rect x={tooltip.x - 10} y={tooltip.y - 60} width="100" height="52" rx="6" fill="white" stroke="#e5e7eb" strokeWidth="1" />
        <text x={tooltip.x + 40} y={tooltip.y - 42} fontSize="11" fontWeight="bold" fill="#111827" textAnchor="middle">Week 3</text>
        <text x={tooltip.x + 40} y={tooltip.y - 27} fontSize="9" fill="#6b7280" textAnchor="middle">Avg feedback time:</text>
        <text x={tooltip.x + 40} y={tooltip.y - 13} fontSize="12" fontWeight="bold" fill="#111827" textAnchor="middle">2.1 days</text>

        {/* X-axis labels */}
        {["April 2", "April 9", "April 16", "April 23", "Apr 30"].map((label, i) => (
          <text key={i} x={30 + i * 90} y="200" fontSize="9" fill="#9ca3af" textAnchor="middle">{label}</text>
        ))}
      </svg>
    </div>
  );
};

// Candidate progress bar
interface ProgressBarProps {
  label: string;
  total: number;
  highlight: number;
}

const ProgressBar = ({ label, total, highlight }: ProgressBarProps) => {
  const sum = total + highlight;
  const grayPct = sum === 0 ? 50 : (total / sum) * 100;
  const highlightPct = sum === 0 ? 50 : (highlight / sum) * 100;

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex items-center gap-1 w-24 shrink-0">
        <svg className="w-3 h-3 text-[#ABD405] fill-current" viewBox="0 0 10 10">
          <polygon points="0,0 10,5 0,10" />
        </svg>
        <span className="text-xs text-gray-600">{label}</span>
      </div>
      <div className="flex items-center gap-0 w-full min-w-0">
        <div
          className="h-8 bg-gray-200 flex items-center justify-center rounded-l-md flex-1 min-w-0"
          style={{ flex: `${grayPct} 1 0%` }}
        >
          <span className="text-sm font-bold text-black truncate">{total}</span>
        </div>
        <div
          className="h-8 bg-[#ABD405] flex items-center justify-center rounded-r-md flex-1 min-w-0"
          style={{ flex: `${highlightPct} 1 0%` }}
        >
          <span className="text-sm font-semibold text-gray-800 truncate">{highlight}</span>
        </div>
      </div>
    </div>
  );
};

// Attention item component
interface AttentionItemProps {
  text: string;
  count: number;
  type?: "clock" | "alert";
}

const AttentionItem = ({ text, count, type = "clock" }: AttentionItemProps) => (
  <div className="flex items-center justify-between border border-gray-200 rounded-md px-4 py-3 mb-2">
    <div className="flex items-center gap-3">
      {type === "clock" ? (
        <svg className="w-5 h-5 text-lime-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <polyline points="12 6 12 12 16 14" strokeWidth="2" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-lime-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
          <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
        </svg>
      )}
      <span className="text-sm text-black">{text}</span>
    </div>
    <span className="text-sm font-bold text-gray-800">{count}</span>
  </div>
);

// Empty state donut chart (just a plain circle, no fill)
const EmptyDonutChart = () => (
  <svg viewBox="0 0 80 80" className="w-20 h-20">
    <circle cx="40" cy="40" r="30" fill="none" stroke="#1f2937" strokeWidth="8" />
    <text x="40" y="45" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111827">0</text>
  </svg>
);

// Empty state KPI card with sparkline
interface EmptyKpiCardProps {
  title: string;
}
const EmptyKpiCard = ({ title }: EmptyKpiCardProps) => (
  <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
    <p className="text-xs font-bold text-black mb-2">{title}</p>
    <div className="flex items-end justify-between">
      <div>
        <p className="text-2xl font-bold text-black">0</p>
        <p className="text-xs text-[#ABD405] mt-1">Data will appear once you start receiving matches.</p>
      </div>
      <Sparkline />
    </div>
  </div>
);

// Empty state KPI card with donut
interface EmptyDonutCardProps {
  title: string;
}
const EmptyDonutCard = ({ title }: EmptyDonutCardProps) => (
  <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
    <p className="text-xs font-bold text-black mb-2">{title}</p>
    <div className="flex items-center justify-between">
      <p className="text-2xl font-bold text-black">0</p>
      <EmptyDonutChart />
    </div>
  </div>
);

// First-time / empty state view
interface EmptyDashboardProps {
  userName?: string;
  onCreateJob: () => void;
  isHiringManager?: boolean;
}
const EmptyDashboard = ({ userName = "Jenna", onCreateJob, isHiringManager = false }: EmptyDashboardProps) => (
  <div className="min-h-screen bg-white p-6 font-sans">
    <h1 className="text-2xl font-bold text-black mb-1">
      Welcome, {userName} 👋 Let&apos;s set up your hiring workflow.
    </h1>
    <p className="text-sm text-gray-600 mb-4">
      You don&apos;t have any active jobs yet. Start by creating your first job post.
    </p>
    {/* <button
      onClick={onCreateJob}
      className="btn-cta text-sm font-semibold px-5 py-2 rounded-md mb-8"
    >
      Create your first job
    </button> */}

    <div className={`grid gap-4 mb-8 ${isHiringManager ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2 lg:grid-cols-4"}`}>
      <EmptyKpiCard title="Average time to next stage" />
      {!isHiringManager && <EmptyKpiCard title="Avg time to close a Job" />}
      <EmptyDonutCard title="In review acceptance rate" />
      <EmptyDonutCard title="Avg match score (shortlisted)" />
    </div>

    <div className="text-center py-10 mb-8">
      <p className="text-base font-bold text-gray-800">Your hiring pipeline is empty</p>
      {/* <p className="text-base text-gray-600 mb-5">Post a job to start receiving matches.</p> */}
      {/* <button
        onClick={onCreateJob}
        className="btn-cta text-sm font-semibold px-5 py-2 rounded-md"
      >
        Create your first job
      </button> */}
    </div>

    <div className={isHiringManager ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 lg:grid-cols-2 gap-4"}>
      <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-200">
        <p className="text-sm font-semibold text-gray-800 mb-3">Needs attention - hiring manager</p>
        <AttentionItem type="clock" text="0 Candidates awaiting feedback" count={0} />
        <AttentionItem type="alert" text="0 review overdue" count={0} />
      </div>
      {!isHiringManager && (
        <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-200">
          <p className="text-sm font-semibold text-gray-800 mb-3">Needs attention - pipeline</p>
          <AttentionItem type="clock" text="0 Hiring managers overdue on feedback" count={0} />
          <AttentionItem type="alert" text="0 job no shortlist candidates" count={0} />
        </div>
      )}
    </div>
  </div>
);

interface HomePageProps {
  userName?: string;
  isFirstTime?: boolean;
  onCreateJob?: () => void;
}

export default function HomePage({
  userName: userNameProp = "Jenna",
  onCreateJob,
}: HomePageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [showEmpty, setShowEmpty] = useState(() => location.state?.fromResetPassword === true);
  const [department, setDepartment] = useState("");
  const [assignedJobs, setAssignedJobs] = useState("");
  const [userGroups, setUserGroups] = useState<string[]>(() => Array.isArray(user?.groups) ? (user?.groups as string[]) : []);

  const userName = user?.name ?? user?.email ?? userNameProp;
  const isHiringManager = userGroups.includes("company_hiring_manager");

  useEffect(() => {
    const loadUserGroups = async () => {
      if (!accessToken) {
        const groups = Array.isArray(user?.groups) ? (user?.groups as string[]) : [];
        setUserGroups(groups);
        return;
      }
      try {
        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
        const groups = ensuredUser.groups ?? [];
        setUserGroups(Array.isArray(groups) ? groups : []);
      } catch {
        const groups = Array.isArray(user?.groups) ? (user?.groups as string[]) : [];
        setUserGroups(groups);
      }
    };
    loadUserGroups();
  }, [accessToken, user?.groups, dispatch]);

  useEffect(() => {
    if (location.state?.fromResetPassword !== true) {
      setShowEmpty(false);
    }
  }, [location.state?.fromResetPassword]);

  const handleCreateJob = () => {
    if (onCreateJob) {
      onCreateJob();
    } else {
      navigate("/create-job");
    }
    setShowEmpty(false);
  };

  if (showEmpty) {
    return (
      <EmptyDashboard
        userName={userName}
        onCreateJob={handleCreateJob}
        isHiringManager={isHiringManager}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white p-6 font-sans">
      {/* Filters */}
      <div className="flex items-center gap-6 mb-6 flex-wrap">
        <div>
          <label className="block text-xs font-bold text-black mb-1">Department</label>
          <div className="relative">
            <select
              className="appearance-none bg-gray-100 border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm font-semibold text-black focus:outline-none focus:ring-1 focus:ring-lime-400 min-w-[160px]"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            >
              <option value="">Select</option>
              <option>Engineering</option>
              <option>Marketing</option>
              <option>Sales</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <DropdownChevron />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-black mb-1">Assigned jobs</label>
          <div className="relative">
            <select
              className="appearance-none bg-gray-100 border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm font-semibold text-black focus:outline-none focus:ring-1 focus:ring-lime-400 min-w-[160px]"
              value={assignedJobs}
              onChange={(e) => setAssignedJobs(e.target.value)}
            >
              <option value="">Select</option>
              <option>Job 1</option>
              <option>Job 2</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <DropdownChevron />
            </div>
          </div>
        </div>

        <div className="ml-auto">
          <label className="block text-xs font-bold text-black mb-1">Date range</label>
          <div className="relative">
            <select className="appearance-none bg-gray-100 border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm font-semibold text-black focus:outline-none focus:ring-1 focus:ring-lime-400 min-w-[220px]">
              <option>10/28/2025 - 11/26/2025</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <DropdownChevron />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 gap-4 mb-6 ${isHiringManager ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
        {/* Card 1 */}
        <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-2">Average time to next stage</p>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-2xl font-bold text-black">2.5 Days</p>
              <p className="text-xs text-[#ABD405] flex items-center gap-1 mt-1">
                <svg className="w-3 h-3" fill="none" stroke="#0CA213" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                12% faster then last month
              </p>
            </div>
            <Sparkline />
          </div>
        </div>

        {!isHiringManager && (
          <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
            <p className="text-base font-bold text-black mb-2">Avg time to close a Job</p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-black">2.5 Days</p>
                <p className="text-xs text-[#ABD405] flex items-center gap-1 mt-1">
                  <svg className="w-3 h-3" fill="none" stroke="#0CA213" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  12% faster then last month
                </p>
              </div>
              <Sparkline />
            </div>
          </div>
        )}

        {/* In review acceptance rate */}
        <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-2">In review acceptance rate</p>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold text-black">80%</p>
            <DonutChart value={80} />
          </div>
        </div>

        {/* Avg match score */}
        <div className="bg-gray-100 rounded-md p-4 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-2">Avg match score (shortlisted)</p>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold text-black">95%</p>
            <DonutChart value={95} />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Feedback time over time */}
        <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-4">Feedback time over time</p>
          <FeedbackChart />
        </div>

        {/* Candidate progress by stage */}
        <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-4">Candidate progress by stage</p>
          <div className="mt-4">
            <ProgressBar label="In review" total={20} highlight={8} />
            <ProgressBar label="Shortlisted" total={40} highlight={4} />
            <ProgressBar label="Hired" total={3} highlight={3} />
          </div>
        </div>
      </div>

      {/* Attention Sections */}
      <div className={isHiringManager ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 lg:grid-cols-2 gap-4"}>
        <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-100">
          <p className="text-base font-bold text-black mb-3">Needs attention - hiring manager</p>
          <AttentionItem type="clock" text="3 Candidates awaiting feedback" count={3} />
          <AttentionItem type="alert" text="1 review overdue" count={1} />
        </div>
        {!isHiringManager && (
          <div className="bg-gray-100 rounded-md p-5 shadow-sm border border-gray-100">
            <p className="text-base font-bold text-black mb-3">Needs attention - pipeline</p>
            <AttentionItem type="clock" text="2 Hiring managers overdue on feedback" count={2} />
            <AttentionItem type="alert" text="1 job no shortlist candidates" count={1} />
          </div>
        )}
      </div>
    </div>
  );
}
